<script setup>
import SettingsSection from '@/components/settings/SettingsSection.vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import { useBranding } from '@/branding';
import { useBrandAssets } from '@/composables/useBrandAssets';

// Images apply as soon as they're uploaded (no Save step).
const IMAGES = [
  {
    kind: 'logo',
    title: 'Logo',
    hint: 'Square, at least 128×128. Sidebar, sign-in page, browser tab and emails.',
    wide: false,
  },
  {
    kind: 'pdf-header',
    title: 'Report header',
    hint: 'Wide banner at the top of generated forms and reports.',
    wide: true,
  },
  {
    kind: 'pdf-footer',
    title: 'Report footer',
    hint: 'Wide banner at the bottom of generated forms and reports.',
    wide: true,
  },
];

const branding = useBranding();
const { busy, upload, reset } = useBrandAssets();
const inputs = {};

function onFile(kind, event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  upload(kind, file);
}
</script>

<template>
  <SettingsSection
    title="Images"
    description="PNG, JPEG or WebP up to 5 MB. Changes apply as soon as you upload."
    hide-actions
  >
    <ul class="images">
      <li v-for="img in IMAGES" :key="img.kind" class="images__row">
        <div class="images__preview" :class="{ 'images__preview--wide': img.wide }">
          <img v-if="branding.assets[img.kind]" :src="branding.assets[img.kind].url" :alt="img.title" />
        </div>
        <div class="images__body">
          <div class="images__title">
            {{ img.title }}
            <UiBadge :variant="branding.assets[img.kind]?.custom ? 'secondary' : 'outline'">
              {{ branding.assets[img.kind]?.custom ? 'Custom' : 'Default' }}
            </UiBadge>
          </div>
          <p class="images__hint">{{ img.hint }}</p>
          <div class="images__actions">
            <v-btn
              variant="outlined"
              size="small"
              prepend-icon="mdi-upload-outline"
              :loading="busy === img.kind"
              @click="inputs[img.kind]?.click()"
            >
              Upload
            </v-btn>
            <v-btn
              v-if="branding.assets[img.kind]?.custom"
              variant="text"
              size="small"
              :disabled="busy === img.kind"
              @click="reset(img.kind)"
            >
              Use default
            </v-btn>
            <input
              :ref="(el) => (inputs[img.kind] = el)"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              hidden
              @change="onFile(img.kind, $event)"
            />
          </div>
        </div>
      </li>
    </ul>
  </SettingsSection>
</template>

<style scoped>
.images {
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid var(--border);
  border-radius: 12px;
}
.images__row {
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 16px;
}
.images__row + .images__row {
  border-top: 1px solid var(--border);
}
.images__preview {
  flex: 0 0 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--subtle);
  overflow: hidden;
}
.images__preview--wide {
  flex-basis: 200px;
}
.images__preview img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.images__body {
  min-width: 0;
}
.images__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
}
.images__hint {
  margin: 2px 0 0;
  font-size: 13px;
  color: var(--muted-foreground);
}
.images__actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}
@media (max-width: 600px) {
  .images__row {
    flex-direction: column;
    align-items: flex-start;
  }
  .images__preview--wide {
    width: 100%;
    flex-basis: auto;
  }
}
</style>
